import { useCallback } from 'react';
import { ExtendedMarkerData, MarkerAction } from '../../../../types/marker';
import { useFormValidation } from '../../../../hooks/useFormValidation';
import logger from '../../../../utils/logger';
import { actionsService } from '../../../../services/supabase/actions.service';
import { alertsService } from '../../../../services/supabase/alerts.service';
import { eventsService } from '../../../../services/events.service';
import { useNotifications } from '../../../../context/NotificationContext';
import { useAppContext } from '../../../../context/AppContext';
import { supabase } from '../../../../config/supabase';
import { markersService } from '../../../../services/supabase/markers.service';

interface UseMarkerSubmitProps {
  mode: 'create' | 'edit';
  marker?: ExtendedMarkerData;
  formData: ExtendedMarkerData;
  originalCategoryId: string | null;
  onSave: (data: ExtendedMarkerData) => Promise<ExtendedMarkerData>;
  onClose: () => void;
  createCustomAction: (type: string, description: string, reminderDate?: Date) => MarkerAction;
  mergeActions: (existingActions: MarkerAction[], newAction: MarkerAction) => MarkerAction[];
  createCategoryChangeAction: (oldCategoryId: string | null, newCategoryId: string | null) => MarkerAction;
}

export const useMarkerSubmit = ({
  mode,
  marker,
  formData,
  originalCategoryId,
  onSave,
  onClose,
  createCustomAction,
  mergeActions,
  createCategoryChangeAction
}: UseMarkerSubmitProps) => {
  const { errors, validateForm, clearErrors } = useFormValidation();
  const { state, dispatch } = useAppContext();
  const { addNotification } = useNotifications();

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Log détaillé pour le début de la soumission
    console.log('📩 SOUMISSION - Début handleSubmit', {
      mode,
      formDataCategoryId: formData.categoryId,
      markerCategoryId: marker?.categoryId,
      timestamp: new Date().toISOString()
    });
    
    if (!validateForm(formData)) {
      return;
    }
    
    // Créer une copie locale des données du formulaire pour éviter les problèmes de rendu
    const _formDataSnapshot = { ...formData };
    
    try {
      const updatedData: ExtendedMarkerData = {
        ...formData,
        updatedAt: new Date()
      };

      // Traitement différent selon le mode (création ou modification)
      if (mode === 'create') {
        // Sauvegarder d'abord le marqueur pour obtenir son ID
        logger.debug('Création d\'un nouveau marqueur avec les données:', {
          ...updatedData,
          actions: updatedData.actions?.length || 0
        });
        
        // Stocker les actions temporaires avant de les supprimer pour la création du marqueur
        const tempActions = [...(updatedData.actions || [])];
        
        console.log('🔍 [DEBUG] Actions avant création marqueur:', {
          tempActionsCount: tempActions.length,
          tempActions: tempActions.map(a => ({
            id: a.id,
            type: a.type,
            description: a.description,
            markerId: a.markerId,
            is_system: a.is_system
          })),
          timestamp: new Date().toISOString()
        });
        
        // Supprimer les actions temporaires pour la création du marqueur
        // (elles seront créées après avec le bon markerId)
        updatedData.actions = [];
        
        try {
          // Créer le marqueur sans les actions
          const newMarker = await onSave(updatedData);
          
          // Si des actions temporaires existent, les créer avec le bon markerId
          if (tempActions.length > 0) {
            logger.debug(`Création de ${tempActions.length} actions pour le marqueur ${newMarker.id}`);
            
            // Créer toutes les actions en parallèle pour plus d'efficacité
            const actionPromises = tempActions.map(async (action) => {
              try {
                // Récupérer les informations utilisateur pour l'action
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) {
                  throw new Error('Utilisateur non authentifié');
                }

                // Récupérer le profil utilisateur pour obtenir agency_id
                const { data: profile } = await supabase
                  .from('profiles')
                  .select('agency_id')
                  .eq('id', user.id)
                  .single();

                console.log('🔍 [DEBUG] Informations utilisateur récupérées:', {
                  userId: user.id,
                  agencyId: profile?.agency_id,
                  timestamp: new Date().toISOString()
                });

                // Créer l'action avec le bon markerId et les informations utilisateur
                const actionData: Omit<MarkerAction, 'id'> = {
                  ...action,
                  markerId: newMarker.id,
                  user_id: user.id,
                  isTemporary: false // Marquer comme permanente
                };
                
                // Supprimer l'id temporaire s'il existe
                if ('id' in actionData) {
                  delete (actionData as any).id;
                }
                
                console.log('🔍 [DEBUG] Avant création action dans Supabase:', {
                  markerId: newMarker.id,
                  actionData: {
                    type: actionData.type,
                    description: actionData.description,
                    markerId: actionData.markerId,
                    is_system: actionData.is_system,
                    status: actionData.status
                  },
                  timestamp: new Date().toISOString()
                });
                
                logger.debug('Création d\'une action pour le nouveau marqueur:', {
                  markerId: newMarker.id,
                  type: actionData.type,
                  description: actionData.description
                });
                
                // Créer l'action dans Supabase
                const newAction = await actionsService.create(actionData);
                
                console.log('🔍 [DEBUG] Action créée dans Supabase:', {
                  actionId: newAction.id,
                  markerId: newMarker.id,
                  type: newAction.type,
                  description: newAction.description,
                  is_system: newAction.is_system,
                  timestamp: new Date().toISOString()
                });
                
                logger.info('✅ Action créée avec succès:', {
                  actionId: newAction.id,
                  markerId: newMarker.id,
                  type: newAction.type,
                  description: newAction.description
                });
                
                // Si une date de rappel est définie, créer une notification et une alerte
                if (action.reminderDate) {
                  logger.info('🔔 Création d\'une notification de rappel', {
                    actionId: newAction.id,
                    reminderDate: typeof action.reminderDate === 'string' 
                      ? action.reminderDate 
                      : action.reminderDate.toISOString(),
                    type: action.type,
                    description: action.description
                  });
                  
                  const category = state.categories.find(cat => cat.id === updatedData.categoryId)?.name || '';
                  
                  // Attendre un court délai pour s'assurer que le marqueur est bien enregistré
                  // et que son adresse est disponible dans la base de données
                  setTimeout(async () => {
                    try {
                      // Récupérer le marqueur depuis la base de données pour avoir l'adresse à jour
                      const { data: markerData, error } = await supabase
                        .from('markers')
                        .select('address')
                        .eq('id', newMarker.id)
                        .single();
                      
                      if (error) {
                        logger.error('Erreur lors de la récupération de l\'adresse du marqueur:', error);
                      }
                      
                      // Utiliser l'adresse récupérée ou celle fournie lors de la création
                      const markerAddress = markerData?.address || updatedData.address;
                      
                      // Créer une notification dans le système existant
                      addNotification({
                        type: 'reminder',
                        title: action.type,
                        message: action.description,
                        actionId: newAction.id,
                        markerId: newMarker.id,
                        markerCategory: category,
                        reminderDate: typeof action.reminderDate === 'string'
                          ? new Date(action.reminderDate)
                          : action.reminderDate,
                        address: markerAddress
                      });
                      
                      // Créer une alerte dans la nouvelle table alerts
                      await alertsService.createActionAlert(
                        newAction.id,
                        action.type,
                        action.description,
                        (action.reminderDate as Date), // On force le type car on sait que reminderDate existe ici (vérifié dans le if)
                        'normal'
                      );
                      
                      logger.info('✅ Alerte et notification créées avec succès pour l\'action:', {
                        actionId: newAction.id,
                        address: markerAddress
                      });
                    } catch (notifError) {
                      logger.error('Erreur lors de la création de la notification:', notifError);
                    }
                  }, 500); // Attendre 500ms pour s'assurer que le marqueur est bien enregistré
                }
                
                return newAction;
              } catch (error) {
                logger.error('Erreur lors de la création d\'une action:', error);
                return null;
              }
            });
            
            // Attendre que toutes les actions soient créées
            const createdActions = await Promise.all(actionPromises);
            const successfulActions = createdActions.filter(a => a !== null);
            
            logger.info(`✅ ${successfulActions.length}/${tempActions.length} actions créées avec succès pour le marqueur ${newMarker.id}`);
            
            // Forcer un rechargement des marqueurs pour mettre à jour l'interface
            logger.debug('Émission d\'un événement REFRESH_MAP_MARKERS après création des actions');
            eventsService.emit('REFRESH_MAP_MARKERS', {
              markerId: newMarker.id,
              newCategoryId: updatedData.categoryId
            });
            
            // Attendre un peu puis rouvrir le popup pour afficher les nouvelles actions
            setTimeout(() => {
              logger.debug('Réouverture du popup après création des actions');
              eventsService.emit('OPEN_MARKER_POPUP', { markerId: newMarker.id });
            }, 200);
          }
        } catch (error) {
          logger.error('Erreur lors de la création du marqueur:', error);
        }
      } else if (mode === 'edit' && marker) {
        // Traitement pour le mode édition (code existant)
        
        // Vérifier si des actions temporaires existent et les confirmer
        if (formData.id) {
          try {
            await actionsService.confirmTemporaryActions(formData.id);
            logger.info(`Actions temporaires confirmées pour le marqueur ${formData.id}`);
          } catch (error) {
            logger.error('Erreur lors de la confirmation des actions temporaires:', error);
          }
        }
        
        // Liste des champs système à exclure de la comparaison
        // Ajout de 'categoryId' car il est déjà géré par une action spécifique
        const systemFields = ['id', 'coordinates', 'actions', 'createdAt', 'updatedAt', 'contacts', 'categoryId', 'firstName', 'address', 'agency_id', 'status', 'imageUrl'];
        
        // Map des noms de champs pour l'affichage
        const fieldDisplayNames: Record<string, string> = {
          name: 'Nom',
          description: 'Description',
          contact_address: 'Adresse de contact',
          phone: 'Téléphone',
          email: 'Email',
          notes: 'Notes'
        };

        // Log pour déboguer les données
        logger.debug('FormData avant filtrage:', {
          formData,
          systemFields,
          allKeys: Object.keys(formData)
        });

        const changes = Object.keys(formData)
          .filter(key => !systemFields.includes(key))
          .filter(key => {
            const typedKey = key as keyof ExtendedMarkerData;
            const currentValue = formData[typedKey];
            const previousValue = marker[typedKey];

            // Log pour déboguer les comparaisons
            logger.debug(`Comparaison pour ${key}:`, {
              current: currentValue,
              previous: previousValue,
              isDifferent: currentValue !== previousValue
            });

            // Ignorer les changements vides (undefined/null/'')
            const isCurrentEmpty = currentValue === undefined || currentValue === null || currentValue === '';
            const isPreviousEmpty = previousValue === undefined || previousValue === null || previousValue === '';
            
            // Si les deux sont vides, pas de changement
            if (isCurrentEmpty && isPreviousEmpty) {
              return false;
            }

            if (currentValue instanceof Date && previousValue instanceof Date) {
              return currentValue.getTime() !== previousValue.getTime();
            }
            
            if (Array.isArray(currentValue) && Array.isArray(previousValue)) {
              return JSON.stringify(currentValue) !== JSON.stringify(previousValue);
            }

            return currentValue !== previousValue;
          })
          .map(key => fieldDisplayNames[key] || key);

        // Log des changements détectés
        logger.debug('Changements détectés:', {
          changes,
          updatedData
        });

        if (changes.length > 0) {
          updatedData.actions = mergeActions(
            formData.actions || [],
            createCustomAction('update', changes.join(', '))
          );
        }
        
        // Si la catégorie a changé, ajouter une action de changement de catégorie
        if (formData.categoryId !== originalCategoryId) {
          logger.info('🔄 handleSubmit: Changement de catégorie confirmé', {
            markerID: marker.id,
            oldCategoryId: originalCategoryId,
            newCategoryId: formData.categoryId,
            actionsAvantFiltrage: (updatedData.actions || []).length,
            actionsDeTypeCategory: (updatedData.actions || []).filter(a => a.type === 'category_change').length
          });
          
          // Ajouter une action de changement de catégorie
          const categoryChangeAction = createCategoryChangeAction(
            originalCategoryId,
            formData.categoryId
          );
          
          updatedData.actions = mergeActions(
            updatedData.actions || [],
            categoryChangeAction
          );
          
          logger.debug('Action de changement de catégorie ajoutée:', {
            action: categoryChangeAction,
            actionsApres: (updatedData.actions || []).length
          });
        }
        
        // Sauvegarder les modifications
        try {
          await onSave(updatedData);
          logger.info('✅ Marqueur mis à jour avec succès:', {
            id: updatedData.id,
            categoryId: updatedData.categoryId,
            actionsCount: updatedData.actions?.length
          });
          
          // Forcer un rechargement des marqueurs pour mettre à jour l'interface
          logger.debug('Émission d\'un événement REFRESH_MAP_MARKERS après mise à jour');
          eventsService.emit('REFRESH_MAP_MARKERS', {
            markerId: updatedData.id,
            newCategoryId: updatedData.categoryId,
            oldCategoryId: originalCategoryId
          });
        } catch (error) {
          logger.error('Erreur lors de la mise à jour du marqueur:', error);
        }
      }
      
      // Recharger le marker depuis la base de données pour avoir les données fraîches
      if (mode === 'edit' && marker) {
        try {
          const freshMarker = await markersService.getById(marker.id);
          if (freshMarker) {
            // Mettre à jour le marker dans le state avec les nouvelles actions
            dispatch({ type: 'UPDATE_MARKER', payload: freshMarker });
            logger.debug('Marker rechargé avec les données fraîches:', { 
              markerId: marker.id, 
              actionsCount: freshMarker.actions?.length || 0 
            });
          }
        } catch (error) {
          logger.error('Erreur lors du rechargement du marker:', error);
        }
      }
      
      // Fermer la modale après la sauvegarde
      onClose();
      
      // Réinitialiser les erreurs
      clearErrors();
    } catch (error) {
      logger.error('Erreur lors de la soumission du formulaire:', error);
    }
  }, [
    mode,
    marker,
    formData,
    originalCategoryId,
    onSave,
    onClose,
    validateForm,
    clearErrors,
    createCustomAction,
    mergeActions,
    createCategoryChangeAction,
    state.categories,
    addNotification,
    dispatch
  ]);

  return {
    errors,
    handleSubmit
  };
};

